import {
  ColDef,
  CsvExportParams,
  ExcelExportParams,
  GetMainMenuItemsParams,
  GetContextMenuItemsParams,
  MenuItemDef,
} from 'ag-grid-community';
import { Theme, ThemeProvider } from '@benzinga/themetron';
import { renderToStaticMarkup } from 'react-dom/server';

import { GridExportParams, MenuItemParams } from './entities';
import { MenuNodeDef } from '@benzinga/widget-tools';
import { BZColumnState } from '@benzinga/ag-grid-utils';

export const getCsvExportParams = (
  { filename = 'BZ Pro', processCellCallback }: GridExportParams,
  columnKeys: string[],
): CsvExportParams => {
  return {
    appendContent: [
      [],
      [
        {
          data: { value: 'Exported from Benzinga Pro' },
        },
      ],
    ],
    columnKeys,
    fileName: `${filename}.csv`,
    processCellCallback,
  };
};

export const getExcelExportParams = (
  { filename = 'BZ Pro', processCellCallback }: GridExportParams,
  columnKeys: string[],
): ExcelExportParams => {
  return {
    appendContent: [
      { cells: [] },
      {
        cells: [
          {
            data: { type: 'String', value: 'Exported from Benzinga Pro' },
          },
        ],
      },
    ],
    columnKeys,
    fileName: `${filename}.xlsx`,
    processCellCallback,
  };
};

export const getAutoSizeAllColumns = ({ api }: MenuItemParams) => ({
  action: () => {
    api.autoSizeAllColumns();
  },
  name: 'Autosize All Columns',
});

export const getAutoSizeThisColumn = ({ api, column }: MenuItemParams) => ({
  action: () => {
    const columnId = column?.getColId();
    if (columnId) {
      api.autoSizeColumns([columnId]);
    }
  },
  name: 'Autosize This Column',
});

export const getExportTable = (menuItemParams: MenuItemParams, exportParams: GridExportParams) => {
  exportParams.exportCallback?.(menuItemParams);
  const { api: gridApi } = menuItemParams;
  const columnKeys = gridApi
    .getAllDisplayedColumns()
    .map(column => column.getColDef().colId ?? column?.getColDef()?.field ?? '');
  const subMenu: MenuItemDef[] = [
    {
      action: () => {
        const csvExportParams = getCsvExportParams(exportParams, columnKeys);
        gridApi.exportDataAsCsv(csvExportParams);
      },
      name: 'CSV Export',
    },
    {
      action: () => {
        const excelExportParams = getExcelExportParams(exportParams, columnKeys);
        gridApi.exportDataAsExcel(excelExportParams);
      },
      name: 'Excel Export',
    },
  ];

  if (exportParams.onFullExport) {
    subMenu.push({
      action: () => exportParams.onFullExport?.(menuItemParams),
      name: 'Full CSV Export',
    });
  }

  return {
    name: 'Export',
    subMenu,
  };
};

const mapToMenuItem = (items: MenuNodeDef[], theme: Theme): (MenuItemDef | string)[] =>
  items.flatMap<MenuItemDef | string>(item => {
    let icon = '';
    let name = '';
    switch (item.type) {
      case 'Divider':
        return ['separator'];
      case 'Item':
        icon = item.icon ? renderToStaticMarkup(<ThemeProvider theme={theme}>{item.icon}</ThemeProvider>) : '';
        if (typeof item.name !== 'string') {
          name = renderToStaticMarkup(<ThemeProvider theme={theme}>{item.name}</ThemeProvider>);
        } else name = item.name;
        return [
          {
            action: item.action ?? (() => undefined),
            icon,
            name,
          },
        ];
      case 'SubMenu':
        icon = item.icon ? renderToStaticMarkup(<ThemeProvider theme={theme}>{item.icon}</ThemeProvider>) : '';
        if (typeof item.name !== 'string') {
          name = renderToStaticMarkup(<ThemeProvider theme={theme}>{item.name}</ThemeProvider>);
        } else name = item.name;
        return [
          {
            icon,
            name,
            subMenu: mapToMenuItem(item.nodes, theme),
          },
        ];
    }
  });

export const mapContextOption = (
  contextOptions: MenuNodeDef[],
  params: GetContextMenuItemsParams,
  overrides: { theme: Theme; colIds: string[] },
): (MenuItemDef | string)[] => {
  const { column } = params;
  if (!overrides.colIds.includes(column?.getColId() ?? '')) return [];
  return mapToMenuItem(contextOptions, overrides.theme);
};

export const getResetAllColumns = ({ api: gridApi }: MenuItemParams) => ({
  action: () => {
    gridApi?.resetColumnState();
  },
  name: 'Reset All Columns',
});

export const absSort = (
  params: GetMainMenuItemsParams,
  getBzState: () => BZColumnState[] | undefined,
  setBzState: (state: BZColumnState[]) => void,
) => ({
  action: () => {
    const colId = params.column?.getColId();
    const state = getBzState();

    const oldState = state?.find(s => s.colId === colId);
    const colState: BZColumnState = oldState
      ? { ...oldState, absSorted: !oldState.absSorted }
      : { absSorted: true, colId };
    const newState = state?.filter(s => s.colId !== colId)?.concat(colState) ?? [colState];

    setBzState(newState);

    const prevDefs = params?.column?.getColDef();
    const columns = params.api.getColumnDefs() as ColDef[];

    params.api.setGridOption(
      'columnDefs',
      columns?.map(c =>
        prevDefs?.field === c.field
          ? {
              ...c,
              comparator: colState.absSorted
                ? (valueA: any, valueB: any) => (valueA === valueB ? 0 : Math.abs(valueA) > Math.abs(valueB) ? 1 : -1)
                : undefined,
            }
          : c,
      ),
    );
  },
  checked: (getBzState()?.find(s => s.colId === params.column?.getColId()) as any)?.absSorted,
  name: 'ABS Sort',
});
